import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Users, Award, Globe, TrendingUp, Rocket,
  Target, Shield, Zap, ArrowRight, MapPin,
  Building2, ChevronRight, Briefcase, GraduationCap,
  Leaf, Heart, Recycle, Lightbulb, Coins, BarChart,
  HandHeart, TreePine, Sparkles, Droplets, Wind, Sun,
  Hammer, Wrench, Truck, Smartphone, Wifi, Database
} from 'lucide-react';

const About: React.FC = () => {
  useEffect(() => {
    document.title = 'À Propos | Proxy Emobility';
  }, []);

  const values = [
    {
      icon: <Globe size={24} />,
      title: 'Vision Globale',
      description: 'Une approche innovante de la mobilité électrique adaptée aux besoins africains.'
    },
    {
      icon: <Award size={24} />,
      title: 'Excellence',
      description: 'Un engagement constant vers la qualité et la performance de nos solutions.'
    },
    {
      icon: <TrendingUp size={24} />,
      title: 'Innovation',
      description: 'La recherche continue de solutions technologiques avancées.'
    },
    {
      icon: <Users size={24} />,
      title: 'Collaboration',
      description: 'Un esprit d\'équipe fort et des partenariats stratégiques durables.'
    }
  ];

  const milestones = [
    {
      year: '2022',
      title: 'Création de Flit App',
      description: 'Lancement de notre vision pour une mobilité électrique accessible en Afrique.'
    },
    {
      year: '2024',
      title: ' Création de Proxy Emobility, Premier Prototype et première livraison',
      description: 'Développement et test de notre première moto électrique Terra.'
    },
    {
      year: '2025',
      title: 'Lancement officiel',
      description: 'Ouverture de showroom, de bureau dans Dakar et lancement lancement du réseau Proxy Power.'
    },
    {
      year: '2023',
      title: 'Innovation Technologique',
      description: 'Introduction de notre système BaaS (Battery-as-a-Service).'
    }
  ];

  const stats = [
    { value: '50+', label: 'Stations de Recharge' },
    { value: '1000+', label: 'Motos Déployées' },
    { value: '10', label: 'Pays Couverts' },
    { value: '95%', label: 'Satisfaction Client' }
  ];

  const team = [
    {
      name: 'Amadou Diallo',
      position: 'CEO & Fondateur',
      bio: 'Visionnaire de la mobilité électrique avec plus de 15 ans d\'expérience dans le secteur automobile.',
      image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg'
    },
    {
      name: 'Marie Koné',
      position: 'Directrice Technique',
      bio: 'Experte en ingénierie électrique et systèmes de batteries, anciennement chez Tesla.',
      image: 'https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg'
    },
    {
      name: 'Omar Sow',
      position: 'Directeur Commercial',
      bio: 'Spécialiste du développement commercial en Afrique avec un fort réseau dans la région.',
      image: 'https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg'
    }
  ];

  const locations = [
    {
      city: 'Dakar',
      country: 'Sénégal',
      address: '123 Avenue de l\'Indépendance',
      type: 'Siège Social'
    },
    {
      city: 'Abidjan',
      country: 'Côte d\'Ivoire',
      address: '45 Boulevard de la République',
      type: 'Bureau Régional'
    },
    {
      city: 'Casablanca',
      country: 'Maroc',
      address: '78 Avenue Mohammed V',
      type: 'Centre R&D'
    }
  ];

  const environmentalImpact = [
    {
      icon: <Leaf size={24} className="text-green-600" />,
      title: "Réduction des Émissions de CO2",
      description: "Nos motos électriques permettent d'éviter l'émission de plus de 5 tonnes de CO2 par an et par véhicule.",
      stat: "5000+ tonnes de CO2 évitées"
    },
    {
      icon: <Recycle size={24} className="text-green-600" />,
      title: "Économie Circulaire",
      description: "Notre programme de recyclage des batteries assure une gestion responsable des ressources.",
      stat: "95% des composants recyclés"
    },
    {
      icon: <Sun size={24} className="text-yellow-500" />,
      title: "Énergie Solaire",
      description: "Nos stations de recharge sont alimentées par l'énergie solaire, réduisant notre empreinte carbone.",
      stat: "100% d'énergie renouvelable"
    },
    {
      icon: <Droplets size={24} className="text-blue-500" />,
      title: "Préservation des Ressources",
      description: "Réduction significative de la consommation d'eau et de la pollution par rapport aux véhicules thermiques.",
      stat: "80% d'économie d'eau"
    }
  ];

  const socialImpact = [
    {
      icon: <HandHeart size={24} className="text-red-500" />,
      title: "Emploi Local",
      description: "Création d'emplois durables dans les communautés locales, particulièrement pour les femmes.",
      stat: "150+ emplois créés"
    },
    {
      icon: <GraduationCap size={24} className="text-blue-600" />,
      title: "Formation et Développement",
      description: "Programmes de formation technique pour développer les compétences locales.",
      stat: "500+ personnes formées"
    },
    {
      icon: <Building2 size={24} className="text-purple-600" />,
      title: "Entrepreneuriat",
      description: "Soutien aux entrepreneurs locaux à travers notre réseau de stations.",
      stat: "25+ entrepreneurs soutenus"
    },
    {
      icon: <Heart size={24} className="text-pink-500" />,
      title: "Santé Communautaire",
      description: "Amélioration de la qualité de l'air et réduction de la pollution sonore dans les zones urbaines.",
      stat: "10+ communautés impactées"
    }
  ];

  const economicImpact = [
    {
      icon: <Coins size={24} className="text-yellow-600" />,
      title: "Réduction des Coûts",
      description: "Économies significatives sur les coûts d'exploitation par rapport aux véhicules thermiques.",
      stat: "60% d'économie en coûts d'exploitation"
    },
    {
      icon: <BarChart size={24} className="text-blue-600" />,
      title: "Croissance Économique",
      description: "Contribution à l'économie locale à travers notre chaîne d'approvisionnement.",
      stat: "5M+ FCFA injectés dans l'économie locale"
    },
    {
      icon: <Truck size={24} className="text-gray-600" />,
      title: "Logistique Optimisée",
      description: "Amélioration de l'efficacité logistique pour nos partenaires commerciaux.",
      stat: "30% d'amélioration de l'efficacité"
    },
    {
      icon: <Lightbulb size={24} className="text-amber-500" />,
      title: "Innovation Locale",
      description: "Stimulation de l'innovation technologique dans la région.",
      stat: "10+ brevets déposés"
    }
  ];

  const technologicalImpact = [
    {
      icon: <Smartphone size={24} className="text-indigo-600" />,
      title: "Applications Mobiles",
      description: "Développement d'applications innovantes pour la gestion de la mobilité.",
      stat: "10,000+ utilisateurs actifs"
    },
    {
      icon: <Wifi size={24} className="text-blue-500" />,
      title: "IoT et Connectivité",
      description: "Déploiement de solutions IoT pour le suivi en temps réel des véhicules et batteries.",
      stat: "1M+ points de données collectés par jour"
    },
    {
      icon: <Database size={24} className="text-purple-600" />,
      title: "Analyse de Données",
      description: "Utilisation du big data pour optimiser les performances et l'autonomie.",
      stat: "98% de précision prédictive"
    },
    {
      icon: <Zap size={24} className="text-yellow-500" />,
      title: "Technologie de Batterie",
      description: "Innovation dans les systèmes de gestion de batterie pour une durée de vie prolongée.",
      stat: "40% d'augmentation de la durée de vie"
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>À Propos | Proxy Emobility - Notre Histoire et Notre Mission</title>
        <meta name="description" content="Découvrez l'histoire de Proxy Emobility, notre mission de révolutionner la mobilité électrique en Afrique et notre impact environnemental, social et économique." />
        <meta name="keywords" content="Proxy Emobility, mobilité électrique, Afrique, développement durable, impact environnemental, impact social, innovation, équipe, valeurs" />
        <link rel="canonical" href="https://proxyemobility.netlify.app/about" />
        
        {/* Schema.org structured data for Organization */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Proxy Emobility",
              "url": "https://proxyemobility.netlify.app/",
              "logo": "https://proxyemobility.netlify.app/proxy-logo.png",
              "description": "Leader de la mobilité électrique en Afrique. Solutions de mobilité durable et accessible.",
              "foundingDate": "2022",
              "founders": [
                {
                  "@type": "Person",
                  "name": "Amadou Diallo"
                }
              ],
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "123 Avenue de l'Indépendance",
                "addressLocality": "Dakar",
                "addressRegion": "Dakar",
                "addressCountry": "Sénégal"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+221 77 123 45 67",
                "contactType": "customer service",
                "email": "hello@proxyemobility.com"
              },
              "sameAs": [
                "https://www.facebook.com/proxyemobility",
                "https://www.twitter.com/proxyemobility",
                "https://www.instagram.com/proxyemobility",
                "https://www.linkedin.com/company/proxyemobility"
              ]
            }
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary-900 text-white py-16 md:py-24 overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 2%, transparent 2.5%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 2%, transparent 2.5%)`,
            backgroundSize: '60px 60px'
          }} />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Notre Mission
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Révolutionner la mobilité en Afrique grâce à des solutions électriques innovantes, 
              durables et accessibles. Nous construisons l'avenir de la mobilité urbaine.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-primary-600 mb-2">{stat.value}</div>
                <div className="text-neutral-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-6">
                Notre Vision
              </h2>
              <div className="space-y-6 text-neutral-600">
                <p>
                  Chez Proxy Emobility, nous imaginons un futur où la mobilité électrique est la norme 
                  en Afrique. Notre vision s'articule autour de trois piliers fondamentaux :
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="p-2 bg-primary-100 rounded-lg mr-4">
                      <Rocket size={24} className="text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-1">Innovation</h3>
                      <p>Développement continu de solutions adaptées au contexte africain.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="p-2 bg-primary-100 rounded-lg mr-4">
                      <Target size={24} className="text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-1">Accessibilité</h3>
                      <p>Des solutions de mobilité abordables pour tous.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="p-2 bg-primary-100 rounded-lg mr-4">
                      <Shield size={24} className="text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-1">Durabilité</h3>
                      <p>Un engagement fort pour un avenir plus vert.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg"
                alt="Vision Proxy Emobility"
                className="rounded-xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Environmental Impact Section */}
      <section id="environmental-impact" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Notre Impact Environnemental
            </h2>
            <p className="text-lg text-neutral-600">
              Engagés pour un avenir plus durable et respectueux de l'environnement
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {environmentalImpact.map((impact, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-green-100 hover:border-green-200 transition-all hover:shadow-xl">
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center text-green-600 mb-6">
                  {impact.icon}
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3">{impact.title}</h3>
                <p className="text-neutral-600 mb-4">{impact.description}</p>
                <div className="text-green-600 font-semibold">{impact.stat}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Impact Section */}
      <section id="social-impact" className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Notre Impact Social
            </h2>
            <p className="text-lg text-neutral-600">
              Créer des opportunités et améliorer la qualité de vie des communautés
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {socialImpact.map((impact, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-red-100 hover:border-red-200 transition-all hover:shadow-xl">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center text-red-600 mb-6">
                  {impact.icon}
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3">{impact.title}</h3>
                <p className="text-neutral-600 mb-4">{impact.description}</p>
                <div className="text-red-600 font-semibold">{impact.stat}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Economic Impact Section */}
      <section id="economic-impact" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Notre Impact Économique
            </h2>
            <p className="text-lg text-neutral-600">
              Stimuler la croissance économique et créer de la valeur
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {economicImpact.map((impact, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-yellow-100 hover:border-yellow-200 transition-all hover:shadow-xl">
                <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center text-yellow-600 mb-6">
                  {impact.icon}
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3">{impact.title}</h3>
                <p className="text-neutral-600 mb-4">{impact.description}</p>
                <div className="text-yellow-600 font-semibold">{impact.stat}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technological Impact Section */}
      <section id="technological-impact" className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Notre Impact Technologique
            </h2>
            <p className="text-lg text-neutral-600">
              Innover pour transformer la mobilité en Afrique
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {technologicalImpact.map((impact, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-blue-100 hover:border-blue-200 transition-all hover:shadow-xl">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 mb-6">
                  {impact.icon}
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3">{impact.title}</h3>
                <p className="text-neutral-600 mb-4">{impact.description}</p>
                <div className="text-blue-600 font-semibold">{impact.stat}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Nos Valeurs
            </h2>
            <p className="text-lg text-neutral-600">
              Ces valeurs fondamentales guident nos actions et définissent notre culture d'entreprise.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3">{value.title}</h3>
                <p className="text-neutral-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section id="history" className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Notre Histoire
            </h2>
            <p className="text-lg text-neutral-600">
              Un parcours d'innovation et de croissance continue depuis notre création.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex mb-8 last:mb-0">
                <div className="w-24 flex-shrink-0 text-right pr-6">
                  <span className="text-lg font-bold text-primary-600">{milestone.year}</span>
                </div>
                <div className="relative pl-6 border-l-2 border-primary-200">
                  <div className="absolute -left-[9px] top-2 w-4 h-4 bg-primary-500 rounded-full"></div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">{milestone.title}</h3>
                  <p className="text-neutral-600">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Notre Équipe
            </h2>
            <p className="text-lg text-neutral-600">
              Des experts passionnés qui dirigent notre mission de transformation de la mobilité.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl group">
                <div className="aspect-w-3 aspect-h-4 relative overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/0 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-xl font-bold mb-1">{member.name}</h3>
                    <p className="text-primary-200">{member.position}</p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-neutral-600">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/careers" 
              className="inline-flex items-center px-8 py-4 bg-primary-600 text-white font-medium rounded-xl transition-colors shadow-lg hover:shadow-xl hover:bg-primary-700"
            >
              Rejoignez Notre Équipe
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Nos Bureaux
            </h2>
            <p className="text-lg text-neutral-600">
              Une présence stratégique pour mieux servir nos clients à travers l'Afrique.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {locations.map((location, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl">
                <div className="flex items-start">
                  <div className="p-2 bg-primary-50 rounded-lg mr-4">
                    <Building2 size={24} className="text-primary-600" />
                  </div>
                  <div>
                    <div className="text-sm text-primary-600 font-medium mb-1">{location.type}</div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {location.city}, {location.country}
                    </h3>
                    <p className="text-neutral-600 mb-4">{location.address}</p>
                    <Link 
                      to="/contact"
                      className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Nous Contacter
                      <ChevronRight size={16} className="ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">
              Prêt à Nous Rejoindre ?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Découvrez comment nous pouvons collaborer pour transformer la mobilité en Afrique.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-4 bg-white text-primary-900 font-medium rounded-xl transition-colors shadow-lg hover:bg-gray-100"
              >
                Contactez-Nous
              </Link>
              <Link 
                to="/careers"
                className="px-8 py-4 bg-primary-800 text-white font-medium rounded-xl transition-colors border border-primary-700 hover:bg-primary-700"
              >
                Voir Nos Offres
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;